// Premium hour / minute / AM-PM picker (2026-08-25 rewrite).
//
// Contract kept identical for every existing consumer (19 callsites):
//   props.value    : "HH:mm" (24h), or "" when unset
//   props.onChange : (v: "HH:mm" | "") => void
//
// The previous native `<input type="time">` implementation opened the
// browser's segmented dropdown (02 / 34 / AM columns) which looked
// unfinished and clashed with the rest of the Step Up filter chrome.
// This rewrite renders a read-only TextField trigger + MUI Popover
// popup. MUI Popover uses a portal + its own onClose, so the historic
// "two pickers opening at once" bug that killed the last custom
// attempt (`ClickAwayListener` racing focus movement) does not apply
// here — each instance owns its own open state and the popover's
// backdrop-click / Escape close semantics are provided by MUI.
//
// Minute step defaults to 5. Non-standard existing minute values (eg
// "02:34" restored from a URL) are preserved: when opening, the
// picker's minute select injects the current custom value as an
// extra option so applying "Apply" without changing it round-trips
// the same "34". Only if the user explicitly picks a different
// minute do the standard 5-min increments replace it.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, IconButton, InputAdornment, Menu, MenuItem, Popover, TextField, Typography, alpha,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// ── palette (matches Class Sessions filter chrome) ───────────────────
const palette = {
  navy:    "#17324D",
  value:   "#263F55",
  blue:    "#4E78A6",
  steel:   "#6F96BE",
  muted:   "#708397",
  border:  "#DCE5EE",
  soft:    "#EAF2F8",
  card:    "#FFFFFF",
};

// ── serialization helpers ───────────────────────────────────────────
// `HH:mm` in/out. Display is `h:mm A` 12-hour.
const parse24 = (v: string): { h24: number; m: number } | null => {
  if (!v || typeof v !== "string") return null;
  const m = v.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min) || h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { h24: h, m: min };
};
const to24 = (h12: number, ampm: "AM" | "PM"): number => {
  const h = ((h12 % 12) + 12) % 12; // 12 → 0
  return ampm === "PM" ? h + 12 : h;
};
const pad2 = (n: number) => String(n).padStart(2, "0");
const format12 = (v: string): string => {
  const parsed = parse24(v);
  if (!parsed) return "";
  const h12 = ((parsed.h24 + 11) % 12) + 1;
  const ampm = parsed.h24 >= 12 ? "PM" : "AM";
  return `${h12}:${pad2(parsed.m)} ${ampm}`;
};

// ── quick-time convenience (spec §8) ─────────────────────────────────
// Same set for both Start and End; consumer intent is served by the
// existing filter chip labels ("From …" / "Until …").
const QUICK_TIMES: Array<{ label: string; value: string }> = [
  { label: "8:00 AM",  value: "08:00" },
  { label: "9:00 AM",  value: "09:00" },
  { label: "12:00 PM", value: "12:00" },
  { label: "3:00 PM",  value: "15:00" },
  { label: "6:00 PM",  value: "18:00" },
];

interface Props {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  size?: "small" | "medium";
  disabled?: boolean;
  /** Standard-step for the minute select (spec §6). Default 5. */
  minuteStep?: number;
  fullWidth?: boolean;
  /** Show a Clear button in the popover footer (default true). */
  clearable?: boolean;
  /** Render "+1d" adornment for overnight-aware callers. */
  nextDay?: boolean;
  sx?: object;
  placeholder?: string;
}

const HOURS_12: number[] = Array.from({ length: 12 }, (_, i) => i + 1);

const PremiumTimePicker: React.FC<Props> = ({
  value,
  onChange,
  label = "Time",
  error = false,
  helperText,
  size = "small",
  disabled = false,
  minuteStep = 5,
  fullWidth = true,
  clearable = true,
  nextDay = false,
  sx = {},
  placeholder,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  // Draft state — as RAW STRINGS so the user can type freely without
  // the field snapping mid-keystroke. Commit / normalize happens only
  // on blur, quick-pick, or Apply. AM/PM stays a boolean-ish enum.
  const [hourText, setHourText] = useState<string>("09");
  const [minuteText, setMinuteText] = useState<string>("00");
  const [draftAmpm, setDraftAmpm] = useState<"AM" | "PM">("AM");

  // Refs for auto-advance (Hour → Minute → AM) and quick-time focus.
  const hourInputRef = useRef<HTMLInputElement | null>(null);
  const minuteInputRef = useRef<HTMLInputElement | null>(null);

  // Dropdown-affordance menus (spec §3 — keep list as a convenience).
  const [hourMenuAnchor, setHourMenuAnchor] = useState<null | HTMLElement>(null);
  const [minuteMenuAnchor, setMinuteMenuAnchor] = useState<null | HTMLElement>(null);

  // Sync draft from the committed value whenever the popover opens.
  useEffect(() => {
    if (!open) return;
    const parsed = parse24(value);
    if (parsed) {
      const h12 = ((parsed.h24 + 11) % 12) + 1;
      setHourText(pad2(h12));
      setMinuteText(pad2(parsed.m));
      setDraftAmpm(parsed.h24 >= 12 ? "PM" : "AM");
    } else {
      setHourText("09");
      setMinuteText("00");
      setDraftAmpm("AM");
    }
  }, [open, value]);

  // Parse the current draft strings into valid numeric values.
  // Returns null when either field is empty or out of range.
  const parsedDraft = useMemo(() => {
    const h = Number(hourText);
    const m = Number(minuteText);
    if (hourText === "" || minuteText === "") return null;
    if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
    if (h < 1 || h > 12) return null;
    if (m < 0 || m > 59) return null;
    return { h, m };
  }, [hourText, minuteText]);

  const hourInvalid = hourText !== "" && (Number.isNaN(Number(hourText)) || Number(hourText) < 1 || Number(hourText) > 12);
  const minuteInvalid = minuteText !== "" && (Number.isNaN(Number(minuteText)) || Number(minuteText) < 0 || Number(minuteText) > 59);
  const canApply = parsedDraft !== null;

  // Dropdown option list — standard step + injected custom minute so
  // an existing "02:34" round-trips even from the menu (spec §3).
  const minuteOptions = useMemo(() => {
    const step = Math.max(1, Math.min(30, minuteStep || 5));
    const set = new Set<number>();
    for (let m = 0; m < 60; m += step) set.add(m);
    const cur = Number(minuteText);
    if (Number.isInteger(cur) && cur >= 0 && cur <= 59) set.add(cur);
    return Array.from(set).sort((a, b) => a - b);
  }, [minuteStep, minuteText]);

  const displayValue = format12(value);

  const closePicker = () => setOpen(false);

  const commit = (h12: number, minute: number, ampm: "AM" | "PM") => {
    const h24 = to24(h12, ampm);
    onChange(`${pad2(h24)}:${pad2(minute)}`);
  };

  const handleApply = () => {
    if (!parsedDraft) return;
    commit(parsedDraft.h, parsedDraft.m, draftAmpm);
    closePicker();
  };
  const handleClear = () => {
    onChange("");
    closePicker();
  };
  const handleQuickPick = (v: string) => {
    onChange(v);
    closePicker();
  };

  // Digits-only text sanitizer (max 2 chars). Empty is kept so the
  // user can clear the field to retype without a snap-back.
  const sanitizeDigits = (raw: string) => raw.replace(/\D/g, "").slice(0, 2);

  const normalizeHourOnBlur = () => {
    if (hourText === "") return;
    const n = Number(hourText);
    if (Number.isNaN(n)) return;
    if (n < 1) { setHourText("01"); return; }
    if (n > 12) { setHourText("12"); return; }
    setHourText(pad2(n));
  };
  const normalizeMinuteOnBlur = () => {
    if (minuteText === "") return;
    const n = Number(minuteText);
    if (Number.isNaN(n)) return;
    if (n < 0) { setMinuteText("00"); return; }
    if (n > 59) { setMinuteText("59"); return; }
    setMinuteText(pad2(n));
  };

  return (
    <>
      <TextField
        ref={anchorRef}
        label={label}
        value={displayValue}
        placeholder={placeholder}
        onClick={disabled ? undefined : () => setOpen(true)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); }
        }}
        fullWidth={fullWidth}
        size={size}
        error={error}
        helperText={helperText}
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        // read-only so the browser never opens its own picker; the
        // real editor is the Popover below.
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {nextDay && (
                <Box
                  component="span"
                  title="Next day (overnight)"
                  sx={{
                    px: 0.6, py: 0.1, mr: 0.5,
                    borderRadius: "6px",
                    fontSize: 10, fontWeight: 700,
                    color: "#c9a96e", bgcolor: "#ffebd3",
                    whiteSpace: "nowrap",
                  }}
                >+1d</Box>
              )}
              <AccessTimeIcon sx={{ fontSize: 18, color: palette.steel }} />
            </InputAdornment>
          ),
        }}
        sx={{
          ...sx,
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            cursor: disabled ? "not-allowed" : "pointer",
            "& fieldset": { borderColor: palette.border },
            "&:hover fieldset": { borderColor: palette.steel },
            "&.Mui-focused fieldset": { borderColor: palette.blue, borderWidth: "2px" },
          },
          "& .MuiOutlinedInput-input": {
            cursor: disabled ? "not-allowed" : "pointer",
            color: palette.value, fontWeight: 700,
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: { xs: "min(340px, calc(100vw - 24px))", sm: 320 },
              p: 2,
              borderRadius: "16px",
              border: `1px solid ${palette.border}`,
              boxShadow: "0 12px 32px rgba(23,50,77,0.14)",
              overflow: "visible",
            },
          },
        }}
      >
        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, color: palette.muted,
          textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.25,
        }}>
          {label && label !== "Time" ? `Select ${label}` : "Select time"}
        </Typography>

        {/* Editable Hour : Minute [AM|PM] row.
            Hour + Minute are TYPEABLE inputs — the user can click into
            "02" and just type "9" then Tab. Dropdown arrows expose the
            same standard list as a convenience (spec §3). Draft state
            is raw strings so a partial "1" doesn't snap to "01" while
            the user is still typing; normalization runs on blur / quick
            pick / Apply only. */}
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
          onKeyDown={(e) => {
            // Enter applies (if valid); Escape closes. Escape's default
            // Popover handling ALSO catches this, but reserving it here
            // prevents a race with input controls.
            if (e.key === "Enter") { e.preventDefault(); if (canApply) handleApply(); }
            if (e.key === "Escape") { e.preventDefault(); closePicker(); }
          }}
        >
          {/* Hour input */}
          <TextField
            size="small"
            value={hourText}
            inputRef={hourInputRef}
            onChange={(e) => setHourText(sanitizeDigits(e.target.value))}
            onFocus={(e) => e.target.select()}
            onBlur={normalizeHourOnBlur}
            onKeyDown={(e) => {
              // Auto-advance to Minute after two digits are typed —
              // stable because we only advance on the 2nd digit keydown
              // when the current text is already 1 char long.
              if (/^\d$/.test(e.key) && hourText.length === 1) {
                setTimeout(() => minuteInputRef.current?.focus(), 0);
              }
            }}
            error={hourInvalid}
            inputProps={{
              inputMode: "numeric", pattern: "[0-9]*",
              maxLength: 2,
              "aria-label": "Hour",
              style: { textAlign: "center", fontWeight: 800, color: palette.navy },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: -0.75 }}>
                  <IconButton
                    size="small" aria-label="Hour options"
                    onClick={(e) => setHourMenuAnchor(e.currentTarget)}
                    sx={{ p: 0.25, color: palette.steel, "&:hover": { color: palette.blue, bgcolor: "transparent" } }}
                  >
                    <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "& fieldset": { borderColor: hourInvalid ? "#D97882" : palette.border },
                "&:hover fieldset": { borderColor: hourInvalid ? "#D97882" : palette.steel },
                "&.Mui-focused fieldset": {
                  borderColor: hourInvalid ? "#D97882" : palette.blue,
                  borderWidth: "2px",
                },
                "&.Mui-focused": {
                  boxShadow: `0 0 0 3px ${alpha(hourInvalid ? "#D97882" : palette.blue, 0.12)}`,
                },
              },
            }}
          />

          {/* Hour dropdown menu (convenience) */}
          <Menu
            anchorEl={hourMenuAnchor}
            open={!!hourMenuAnchor}
            onClose={() => setHourMenuAnchor(null)}
            PaperProps={{ sx: { maxHeight: 260, mt: 0.5 } }}
          >
            {HOURS_12.map((h) => (
              <MenuItem
                key={h}
                selected={Number(hourText) === h}
                onClick={() => { setHourText(pad2(h)); setHourMenuAnchor(null); }}
                sx={{ justifyContent: "center", fontWeight: 700, fontSize: 13, minWidth: 72 }}
              >
                {pad2(h)}
              </MenuItem>
            ))}
          </Menu>

          <Typography sx={{ fontSize: 20, fontWeight: 800, color: palette.muted, lineHeight: 1 }}>:</Typography>

          {/* Minute input */}
          <TextField
            size="small"
            value={minuteText}
            inputRef={minuteInputRef}
            onChange={(e) => setMinuteText(sanitizeDigits(e.target.value))}
            onFocus={(e) => e.target.select()}
            onBlur={normalizeMinuteOnBlur}
            onKeyDown={(e) => {
              // No auto-advance from Minute — the AM/PM segmented
              // control isn't an <input>, so Tab is the natural move.
              if (e.key === "Tab" && !e.shiftKey) {
                // Let normal Tab flow; blur handler will normalize.
              }
            }}
            error={minuteInvalid}
            inputProps={{
              inputMode: "numeric", pattern: "[0-9]*",
              maxLength: 2,
              "aria-label": "Minute",
              style: { textAlign: "center", fontWeight: 800, color: palette.navy },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: -0.75 }}>
                  <IconButton
                    size="small" aria-label="Minute options"
                    onClick={(e) => setMinuteMenuAnchor(e.currentTarget)}
                    sx={{ p: 0.25, color: palette.steel, "&:hover": { color: palette.blue, bgcolor: "transparent" } }}
                  >
                    <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "& fieldset": { borderColor: minuteInvalid ? "#D97882" : palette.border },
                "&:hover fieldset": { borderColor: minuteInvalid ? "#D97882" : palette.steel },
                "&.Mui-focused fieldset": {
                  borderColor: minuteInvalid ? "#D97882" : palette.blue,
                  borderWidth: "2px",
                },
                "&.Mui-focused": {
                  boxShadow: `0 0 0 3px ${alpha(minuteInvalid ? "#D97882" : palette.blue, 0.12)}`,
                },
              },
            }}
          />

          {/* Minute dropdown menu (convenience) */}
          <Menu
            anchorEl={minuteMenuAnchor}
            open={!!minuteMenuAnchor}
            onClose={() => setMinuteMenuAnchor(null)}
            PaperProps={{ sx: { maxHeight: 260, mt: 0.5 } }}
          >
            {minuteOptions.map((m) => (
              <MenuItem
                key={m}
                selected={Number(minuteText) === m}
                onClick={() => { setMinuteText(pad2(m)); setMinuteMenuAnchor(null); }}
                sx={{ justifyContent: "center", fontWeight: 700, fontSize: 13, minWidth: 72 }}
              >
                {pad2(m)}
              </MenuItem>
            ))}
          </Menu>

          {/* AM / PM segmented control (unchanged premium look) */}
          <Box sx={{
            display: "flex", p: "3px", borderRadius: "10px",
            bgcolor: palette.soft, border: `1px solid ${palette.border}`, flexShrink: 0,
          }}>
            {(["AM", "PM"] as const).map((v) => {
              const active = draftAmpm === v;
              return (
                <Box
                  key={v}
                  role="button" tabIndex={0}
                  onClick={() => setDraftAmpm(v)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDraftAmpm(v); } }}
                  sx={{
                    px: 1.25, py: 0.6, borderRadius: "8px", cursor: "pointer",
                    fontSize: 12, fontWeight: 800, minWidth: 34, textAlign: "center",
                    color: active ? "#FFFFFF" : palette.navy,
                    bgcolor: active ? palette.blue : "transparent",
                    boxShadow: active ? `0 2px 8px ${alpha(palette.blue, 0.28)}` : "none",
                    transition: "all 0.15s",
                    outline: "none",
                    "&:hover": { bgcolor: active ? palette.blue : alpha(palette.blue, 0.10) },
                    "&:focus-visible": { outline: `2px solid ${palette.blue}`, outlineOffset: 2 },
                  }}
                  aria-pressed={active}
                  aria-label={v}
                >
                  {v}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Inline validation hint — subtle, doesn't shift layout. */}
        {(hourInvalid || minuteInvalid) && (
          <Typography sx={{ mt: 0.75, fontSize: 11, fontWeight: 700, color: "#B75F68" }}>
            {hourInvalid ? "Hour must be 1–12." : "Minute must be 0–59."}
          </Typography>
        )}

        {/* Quick times */}
        <Typography sx={{
          fontSize: 10.5, fontWeight: 800, color: palette.muted,
          textTransform: "uppercase", letterSpacing: "0.08em", mt: 2, mb: 1,
        }}>
          Quick times
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {QUICK_TIMES.map((q) => (
            <Box
              key={q.value}
              role="button" tabIndex={0}
              onClick={() => handleQuickPick(q.value)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleQuickPick(q.value); } }}
              sx={{
                px: 1.25, py: 0.5, borderRadius: "10px", cursor: "pointer",
                fontSize: 11.5, fontWeight: 700,
                color: palette.value, bgcolor: palette.card,
                border: `1px solid ${palette.border}`,
                transition: "all 0.15s",
                outline: "none",
                "&:hover": { bgcolor: palette.soft, borderColor: palette.steel, color: palette.blue },
                "&:focus-visible": { outline: `2px solid ${palette.blue}`, outlineOffset: 2 },
              }}
            >{q.label}</Box>
          ))}
        </Box>

        {/* Footer */}
        <Box sx={{
          mt: 2, pt: 1.5, borderTop: `1px solid ${palette.border}`,
          display: "flex", justifyContent: "space-between", gap: 1,
        }}>
          {clearable ? (
            <Button
              size="small" onClick={handleClear}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                color: palette.muted, px: 1.5,
                "&:hover": { color: palette.navy, bgcolor: alpha(palette.navy, 0.05) },
              }}
            >Clear</Button>
          ) : <Box />}
          <Button
            size="small" variant="contained" onClick={handleApply}
            disabled={!canApply}
            sx={{
              textTransform: "none", fontWeight: 800, borderRadius: "10px",
              bgcolor: palette.blue, boxShadow: "none",
              px: 2,
              "&:hover": { bgcolor: palette.navy, boxShadow: `0 4px 12px ${alpha(palette.blue, 0.30)}` },
              "&.Mui-disabled": { bgcolor: alpha(palette.steel, 0.24), color: "#fff" },
            }}
          >Apply</Button>
        </Box>
      </Popover>
    </>
  );
};

export default PremiumTimePicker;
